// Otto's record of what the USER themselves DECIDED — the filters they chose and applied, the ones they
// dropped — as distinct from what Otto merely OFFERED.
//
// WHY THIS EXISTS AS ITS OWN CHANNEL. Two things already reach the model that look like they should carry
// this, and neither does:
//   - `active_filters` says WHAT is in effect, but not that a human chose it, or when. A filter that was
//     already on the dashboard when the session opened is indistinguishable from one the user picked ten
//     seconds ago. (It is also deliberately VALUE-LESS on the wire — every applied dimension is the literal
//     string "applied" — because that payload feeds the fragile interpret prompt; see buildActiveContext.)
//   - `history` is the CONVERSATION, and an assistant turn reads "Filtering to Compliance…" whether the
//     user then applied it, edited the pills, or walked away. It records the OFFER, never the outcome.
// So "i need an excel", asked right after the user had filtered to Code Compliance, produced a plan with a
// redundant "filter to code compliance issues" step: nothing in the payload said that decision was already
// made, so the planner re-derived it from the unanswered clarification still sitting in history
// (trace ce5d8a7f-9400-4018-9670-a120f62def8c).
//
// DERIVED, NOT INSTRUMENTED. We snapshot the applied state each turn and DIFF it against the previous
// turn's. Whatever changed between two messages changed because the USER changed it — via Otto's Apply
// button or via the filter window, both of which are user decisions. That makes the record source-agnostic
// (a manual filter counts exactly as much as an Otto one, which is the point) and keeps it out of every
// capability's apply path, so a new capability inherits it for free.
//
// The first turn of a conversation records a snapshot but emits NO decisions: with nothing to diff against
// we cannot tell a fresh choice from pre-existing state, and claiming the user "just applied" a filter they
// set last week is worse than saying nothing. We only ever report changes we actually observed.

const KEY = "otto.decisions.v1";

// CROSS-REPO WIRE CONTRACT — parity-checked against filter-ai's UserState / AppliedFilter / UserDecision
// (app/core/schemas/envelope.py) by otto-sync's `xrepo-user-state` gate.
//
// Drift here is SILENT, which is why it needs a gate rather than a comment. Pydantic DROPS unknown keys, so
// renaming a field on either side does not 422 and does not log: the backend simply receives an empty
// `recent_decisions`, the planner stops seeing that the user decided anything, and it goes back to planning
// steps the user already performed — the exact bug this module exists to fix, silently reintroduced.
export const USER_STATE_KEYS = ["applied", "recent_decisions"];
export const APPLIED_FILTER_KEYS = ["label", "values"];
export const USER_DECISION_KEYS = ["action", "summary", "turns_ago"];

// Ring buffer of observed decisions. Small on purpose: this rides on every request and its whole job is
// recency — a decision fifteen turns back is ambient state, not something the user "just did".
const MAX_KEPT = 6;
// How many ride along on a request. Fewer than we keep, so the tail is available for a future consumer
// (e.g. "undo what I just did") without widening the prompt.
const MAX_SENT = 4;
// After this many messages a decision stops counting as one and becomes ordinary context. Without an age
// cutoff a single early choice would ride along forever, aging into "12 messages ago: applied …" — noise
// that competes with the actual recent decision. Dropping it loses nothing: while the filter is still on,
// `applied` keeps reporting it, which is what stops the planner re-planning it.
const MAX_AGE_TURNS = 6;

// Longest a single decision summary may get before we elide the tail. Bounded because it reaches a prompt.
const MAX_SUMMARY = 240;

// localStorage, matching chatStore: Otto's conversation survives navigation and reload, and a plan can
// NAVIGATE between pages mid-run, so a store that died on unmount would lose the decision the user made
// one step earlier — exactly the case this exists to fix.
const storage = () => {
    try {
        return typeof window !== "undefined" ? window.localStorage : null;
    } catch (e) {
        return null;
    }
};

const read = () => {
    const s = storage();
    if (!s) return null;
    try {
        const raw = s.getItem(KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        if (!data || typeof data !== "object") return null;
        return {
            // `null` (never snapshotted) is meaningfully different from `[]` (snapshotted, nothing applied):
            // only the latter can produce a "just applied" diff. Preserve the distinction across reloads.
            snapshot: Array.isArray(data.snapshot) ? data.snapshot : null,
            log: Array.isArray(data.log) ? data.log : [],
            turn: typeof data.turn === "number" ? data.turn : 0
        };
    } catch (e) {
        return null; // corrupt / unreadable — start fresh rather than break the turn
    }
};

const write = state => {
    const s = storage();
    if (!s) return;
    try {
        s.setItem(KEY, JSON.stringify(state));
    } catch (e) {
        /* quota / blocked — best-effort, never break a turn over the decision log */
    }
};

// Wipe the record. Called by the chat's Clear button: the decisions belong to the conversation being
// wiped, and carrying them into a fresh chat would make Otto claim the user "just" did something they did
// in a conversation they explicitly threw away.
export const clearDecisionLog = () => {
    const s = storage();
    if (!s) return;
    try {
        s.removeItem(KEY);
    } catch (e) {
        /* best-effort */
    }
};

const norm = v => String(v == null ? "" : v);

// An applied-state entry keyed for comparison: label -> the set of value names under it.
const byLabel = entries => {
    const map = new Map();
    (entries || []).forEach(e => {
        if (!e || !e.label) return;
        const values = Array.isArray(e.values) ? e.values.map(norm).filter(Boolean) : [];
        // A label can legitimately repeat (two wildcards on one field); union rather than overwrite.
        const prev = map.get(e.label) || [];
        map.set(e.label, prev.concat(values));
    });
    return map;
};

const summarize = (label, values) => {
    const text = values && values.length ? `${label} = ${values.join(", ")}` : label;
    return text.length > MAX_SUMMARY ? `${text.slice(0, MAX_SUMMARY - 1)}…` : text;
};

/**
 * Diff two applied-state snapshots into the decisions they imply.
 *
 * A label gaining values (or appearing) is an "applied"; losing values (or disappearing) is a "removed".
 * A value-less entry (a wildcard, or a dimension whose names could not be resolved) is compared on its
 * LABEL alone — it can still tell us the user turned that filter on or off, which is the part the planner
 * needs, even when we cannot name what they set it to.
 *
 * Exported for tests: this is the whole inference, and it must stay legible.
 */
export const diffAppliedState = (prev, next) => {
    const before = byLabel(prev);
    const after = byLabel(next);
    const decisions = [];

    after.forEach((values, label) => {
        if (!before.has(label)) {
            decisions.push({ action: "applied", summary: summarize(label, values) });
            return;
        }
        const had = new Set(before.get(label));
        const added = values.filter(v => !had.has(v));
        if (added.length) decisions.push({ action: "applied", summary: summarize(label, added) });
    });

    before.forEach((values, label) => {
        if (!after.has(label)) {
            decisions.push({ action: "removed", summary: summarize(label, values) });
            return;
        }
        const kept = new Set(after.get(label));
        const gone = values.filter(v => !kept.has(v));
        if (gone.length) decisions.push({ action: "removed", summary: summarize(label, gone) });
    });

    return decisions;
};

/**
 * Record this turn's applied state and return the `user_state` payload to send with it.
 *
 * Returns `{ applied, recent_decisions }`, or null when there is nothing to say (no filters applied and
 * nothing recently decided) — an absent field keeps the request byte-identical to today's for the common
 * unfiltered case, and gives the backend an unambiguous "no information" rather than an empty shape.
 *
 * `turns_ago` is 1 for a decision observed immediately before this message, counting up from there, so the
 * consumer can weigh recency without needing wall-clock timestamps (which would drift against a chat the
 * user left open overnight).
 */
export const recordAppliedState = applied => {
    const entries = Array.isArray(applied) ? applied : [];
    const state = read() || { snapshot: null, log: [], turn: 0 };
    const turn = state.turn + 1;

    // First observation of this conversation: snapshot only. See the header — a null->applied diff would
    // report pre-existing state as a fresh decision.
    const observed = state.snapshot === null ? [] : diffAppliedState(state.snapshot, entries);

    const ageOf = d => Math.max(1, turn - d.turn + 1);
    const log = state.log
        .concat(observed.map(d => ({ ...d, turn })))
        .filter(d => ageOf(d) <= MAX_AGE_TURNS)
        .slice(-MAX_KEPT);

    write({ snapshot: entries, log, turn });

    const recent = log
        .slice(-MAX_SENT)
        .map(d => ({ action: d.action, summary: d.summary, turns_ago: ageOf(d) }));

    if (!entries.length && !recent.length) return null;
    return { applied: entries, recent_decisions: recent };
};
